'use client';

import { useTranslations } from 'next-intl';
import { homeAssetSrc } from 'components/landing/v3/homeAssetSrc';
import CustomLink from 'components/shared/CustomLink';
import {
  CustomImage,
  getSpeedsizeImageUrl,
} from 'components/shared/custom-image/CustomImage';
import { GIFTING_VOUCHER_PATH } from 'lib/gifting/path';
import { ringProUsAsset } from '../ringProUsAsset';
import styles from './RingProUsPay.module.css';

const bnplLogos = ringProUsAsset('pay/bnpl-logos.png');
const heroesRing = ringProUsAsset('pay/pay-heroes-card.png');
const hsaPhoto = ringProUsAsset('pay/pay-hsa.png');
// This stack was never copied into ring-pro-us/v2. It still lives beside the
// first homepage set. The phone file is those same pixels, reframed so the
// stack hangs off the top and sits on the right.
const giftCards = homeAssetSrc('web_v2/ring-pro-us/pay/pay-gift-cards-pro.png');
const giftCardsMobile = homeAssetSrc(
  'web_v2/ring-pro-us/pay/pay-gift-cards-pro-mobile.png',
);

/** Width after the cache-buster. getSpeedsizeImageUrl drops /w_ when src has ?v=. */
const giftStackUrl = (src: string, width: number) =>
  `${getSpeedsizeImageUrl(src)}/w_${width}`;

/**
 * "Pay your way." (Figma 2561:12807): centred header, three 464 x 480
 * cards — HSA/FSA, buy now pay later logos, heroes — each with its
 * copy on a frosted band, then the gifting banner.
 */
export const RingProUsPay = () => {
  const t = useTranslations('homepage');
  const k = (key: string) => `homepage.ringProUs.pay.${key}`;

  const copy = (id: 'hsa' | 'payLater' | 'heroes' | 'gift') => (
    <span className={styles.copy}>
      <span className={styles.cardTitle}>{t(k(`${id}.title`))}</span>
      <span className={styles.cardBody}>{t(k(`${id}.body`))}</span>
      <span className={styles.btn}>{t(k(`${id}.cta`))}</span>
    </span>
  );

  return (
    <section className={styles.section} aria-labelledby="ring-pro-us-pay-title">
      <div className={styles.inner}>
        <header className={styles.head}>
          <h2 id="ring-pro-us-pay-title" className={styles.title}>
            {t(k('title'))}
          </h2>
          <p className={styles.sub}>{t(k('sub'))}</p>
        </header>

        {/* Desktop: card grid with the gift banner under it. Phone: one
            horizontal rail of four cards (mobile Figma 2736:18). */}
        <div className={styles.rail}>
          <ul className={styles.grid}>
            <li className={styles.cell}>
              <CustomLink
                href="/hsa-fsa"
                className={`${styles.card} ${styles.cardHsa}`}
                data-buttontype="ring_pro_us_pay_hsa"
              >
                <span className={styles.hsaMedia} aria-hidden>
                  <CustomImage
                    className={styles.hsaImg}
                    src={hsaPhoto}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 100vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandHsa}`}
                  aria-hidden
                />
                {copy('hsa')}
              </CustomLink>
            </li>

            <li className={styles.cell}>
              <CustomLink
                href="/ring-pro/buy"
                className={styles.card}
                data-buttontype="ring_pro_us_pay_later"
              >
                <span className={styles.spheres} aria-hidden>
                  <CustomImage
                    className={styles.bnplImg}
                    src={bnplLogos}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 80vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandPay}`}
                  aria-hidden
                />
                {copy('payLater')}
              </CustomLink>
            </li>

            <li className={styles.cell}>
              <CustomLink
                href="/heroes"
                className={styles.card}
                data-buttontype="ring_pro_us_pay_heroes"
              >
                <span className={styles.heroesMedia} aria-hidden>
                  <CustomImage
                    className={styles.heroesImg}
                    src={heroesRing}
                    alt=""
                    fill
                    sizes="(min-width: 992px) 464px, 100vw"
                  />
                </span>
                <span
                  className={`${styles.band} ${styles.bandHeroes}`}
                  aria-hidden
                />
                {copy('heroes')}
              </CustomLink>
            </li>
          </ul>

          <CustomLink
            href={GIFTING_VOUCHER_PATH}
            className={styles.gift}
            data-buttontype="ring_pro_us_pay_gift"
          >
            {copy('gift')}
            {/* One request: the source matches phones, the img is the desktop
                fallback. display:none would still download both. */}
            <span className={styles.giftCards} aria-hidden>
              <picture>
                <source
                  media="(max-width: 767px)"
                  sizes="360px"
                  srcSet={`${giftStackUrl(
                    giftCardsMobile,
                    480,
                  )} 480w, ${giftStackUrl(giftCardsMobile, 750)} 750w`}
                />
                <img
                  className={styles.giftCardsImg}
                  src={giftStackUrl(giftCards, 640)}
                  srcSet={`${giftStackUrl(giftCards, 384)} 384w, ${giftStackUrl(
                    giftCards,
                    640,
                  )} 640w`}
                  sizes="284px"
                  alt=""
                  width={1328}
                  height={1760}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </span>
          </CustomLink>
        </div>
      </div>
    </section>
  );
};

export default RingProUsPay;
